import { useMutation, useQuery } from "@tanstack/react-query";
import { generateHookExports } from "api/generateExports";
import { createCRUDService } from "api/queryService";
import { API_CONFIG } from "services/apiConfig";
import axios from "../axios";
import { formatPayloadIfNeeded } from "api/utils";
import { showToast } from "services/utils";

let baseData = API_CONFIG.MONITORING_AND_MEASURING_OF_PRODUCT;
const BASE_URL = baseData?.path;
const QUERY_KEY = baseData?.queryKey;

const mamOfProductService = createCRUDService({
  baseUrl: BASE_URL,
  queryKey: QUERY_KEY,
  custom: {
    getPlanProduct: (options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, "getPlanProduct"],
        queryFn: async () => {
          const res = await axios.get(`${BASE_URL}/get-plan-product`);
          return res.data;
        },
        retry: options.retry ?? 1,
        enabled: options.enabled ?? true,
        ...options,
      });
    },
    getAllProduct: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, "getAllProduct", params],
        queryFn: async () => {
          const res = await axios.get(`${BASE_URL}/get-all-product`, {
            params,
          });
          return res.data;
        },
        retry: options.retry ?? 1,
        enabled: options.enabled ?? true,
        ...options,
      });
    },
    getProductSpecific: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, "getProductSpecific", params],
        queryFn: async () => {
          const res = await axios.get(`${BASE_URL}/get-product-specific`, {
            params,
          });
          return res.data;
        },
        retry: options.retry ?? 1,
        enabled: options.enabled ?? true,
        ...options,
      });
    },
    createPlan: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .post(`${BASE_URL}/create-plan`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    updatePlan: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .put(`${BASE_URL}/update-plan`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    updateFinalPlan: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .put(`${BASE_URL}/update-final-plan`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    updateFinalSpecific: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .put(`${BASE_URL}/update-final-specific`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    createInspection: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .post(`${BASE_URL}/create-inspection`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    updateInspection: () => {
      return useMutation({
        mutationFn: ({ id, payload }) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .post(`${BASE_URL}/update-inspection/${id}`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    deletePlanProcess: () => {
      return useMutation({
        mutationFn: ({ payload }) => {
          const data = formatPayloadIfNeeded(payload);
          return axios
            .put(`${BASE_URL}/inspection-plan-delete-process`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    getByInspectionId: (id, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, "getByInspectionId", id],
        queryFn: async () => {
          const res = await axios.get(`${BASE_URL}/get-by-id-inspection/${id}`);
          return res.data;
        },
        retry: options.retry ?? 1,
        enabled: options.enabled ?? true,
        ...options,
      });
    },
    updateReport: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .put(`${BASE_URL}/update-report`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
    updateSterilePlan: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(
            payload,
            payload?.isCustomImage ?? false,
          );
          return axios
            .put(`${BASE_URL}/update-sterile-plan`, data, {
              headers:
                data instanceof FormData
                  ? { "Content-Type": "multipart/form-data" }
                  : {},
            })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        },
      });
    },
  },
});

export const {
  mamOfProductList,
  mamOfProductCreate,
  mamOfProductGetById,
  mamOfProductUpdate,
  mamOfProductUpdateSpecific,
  mamOfProductDownload,
  mamOfProductDelete,
  mamOfProductCount,
  mamOfProductGetPlanProduct,
  mamOfProductGetAllProduct,
  mamOfProductGetProductSpecific,
  mamOfProductCreatePlan,
  mamOfProductUpdatePlan,
  mamOfProductUpdateFinalPlan,
  mamOfProductUpdateFinalSpecific,
  mamOfProductCreateInspection,
  mamOfProductUpdateInspection,
  mamOfProductGetByInspectionId,
  mamOfProductUpdateReport,
  mamOfProductUpdateSterilePlan,
  mamOfProductDeletePlanProcess,
} = generateHookExports(mamOfProductService, "mamOfProduct");
